import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
    MapPin, 
    Truck,
    Package,
    Navigation,
    RefreshCw,
    Eye
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons
const originIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

const destinationIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

// City coordinates (approximate centers)
const CITY_COORDS = {
    'Los Angeles': [34.0522, -118.2437],
    'Chicago': [41.8781, -87.6298],
    'Dallas': [32.7767, -96.7970],
    'Atlanta': [33.7490, -84.3880],
    'Phoenix': [33.4484, -112.0740],
    'Denver': [39.7392, -104.9903],
    'Seattle': [47.6062, -122.3321],
    'Miami': [25.7617, -80.1918],
    'New York': [40.7128, -74.0060],
    'Boston': [42.3601, -71.0589],
    'Detroit': [42.3314, -83.0458],
    'Houston': [29.7604, -95.3698],
    'Portland': [45.5155, -122.6789],
    'Memphis': [35.1495, -90.0490],
    'Nashville': [36.1627, -86.7816],
    'Charlotte': [35.2271, -80.8431],
    'San Francisco': [37.7749, -122.4194],
    'Las Vegas': [36.1699, -115.1398],
    'Minneapolis': [44.9778, -93.2650],
    'Salt Lake City': [40.7608, -111.8910]
};

const getCityCoords = (city) => {
    return CITY_COORDS[city] || [39.8283, -98.5795]; // Default to center of US
};

export default function RoutesPage() {
    const [loads, setLoads] = useState([]);
    const [allLoads, setAllLoads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLoad, setSelectedLoad] = useState(null);
    const [showAllLoads, setShowAllLoads] = useState(false);

    useEffect(() => {
        fetchLoads();
    }, []);

    const fetchLoads = async () => {
        setLoading(true);
        try {
            // Fetch in-transit loads
            const inTransitRes = await fetch(`${API_URL}/api/loads?status=in_transit`, {
                credentials: 'include'
            });
            // Fetch all loads
            const allRes = await fetch(`${API_URL}/api/loads`, {
                credentials: 'include'
            });

            if (inTransitRes.ok) {
                const data = await inTransitRes.json();
                setLoads(data);
            }
            if (allRes.ok) {
                const data = await allRes.json();
                setAllLoads(data);
            }
        } catch (error) {
            console.error('Failed to fetch loads:', error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: '#eab308',
            booked: '#f97316',
            in_transit: '#3b82f6',
            delivered: '#22c55e',
            cancelled: '#ef4444'
        };
        return colors[status] || '#71717a';
    };

    const displayLoads = showAllLoads ? allLoads : loads;

    // Calculate map bounds based on loads
    const getMapCenter = () => {
        if (selectedLoad) {
            const origin = getCityCoords(selectedLoad.origin_city);
            const dest = getCityCoords(selectedLoad.destination_city);
            return [(origin[0] + dest[0]) / 2, (origin[1] + dest[1]) / 2];
        }
        return [39.8283, -98.5795]; // Center of US
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="routes-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Route Planning</h1>
                    <p className="text-zinc-500 text-sm mt-1">Track and visualize your active shipments</p>
                </div>
                <div className="flex gap-3">
                    <Button 
                        variant={showAllLoads ? "default" : "outline"}
                        onClick={() => setShowAllLoads(!showAllLoads)}
                        className={showAllLoads ? "bg-orange-500 hover:bg-orange-600" : "border-zinc-700"}
                        data-testid="toggle-all-loads-btn"
                    >
                        <Eye className="w-4 h-4 mr-2" />
                        {showAllLoads ? 'Show In Transit' : 'Show All Loads'}
                    </Button>
                    <Button 
                        onClick={fetchLoads}
                        disabled={loading}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="refresh-routes-btn"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Map Area */}
                <Card className="lg:col-span-2 bg-zinc-900/50 border-zinc-800" data-testid="map-card">
                    <CardHeader className="border-b border-zinc-800 pb-4">
                        <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                            <Navigation className="w-5 h-5 text-orange-500" />
                            Live Fleet Map
                            <Badge variant="outline" className="ml-auto border-zinc-700 text-zinc-400">
                                {displayLoads.length} routes
                            </Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="h-[500px] rounded-b-sm overflow-hidden">
                            <MapContainer 
                                center={getMapCenter()} 
                                zoom={4} 
                                style={{ height: '100%', width: '100%' }}
                                className="z-0"
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                                    url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                                />
                                {displayLoads.map((load) => {
                                    const originCoords = getCityCoords(load.origin_city);
                                    const destCoords = getCityCoords(load.destination_city);
                                    const isSelected = selectedLoad?.load_id === load.load_id;
                                    
                                    return (
                                        <div key={load.load_id}>
                                            {/* Origin Marker */}
                                            <Marker 
                                                position={originCoords} 
                                                icon={originIcon}
                                                eventHandlers={{
                                                    click: () => setSelectedLoad(load)
                                                }}
                                            >
                                                <Popup className="dark-popup">
                                                    <div className="text-sm">
                                                        <strong className="text-green-600">Origin</strong><br/>
                                                        {load.origin_city}, {load.origin_state}<br/>
                                                        <span className="text-gray-500">Load: {load.load_id}</span>
                                                    </div>
                                                </Popup>
                                            </Marker>
                                            
                                            {/* Destination Marker */}
                                            <Marker 
                                                position={destCoords} 
                                                icon={destinationIcon}
                                                eventHandlers={{
                                                    click: () => setSelectedLoad(load)
                                                }}
                                            >
                                                <Popup>
                                                    <div className="text-sm">
                                                        <strong className="text-red-600">Destination</strong><br/>
                                                        {load.destination_city}, {load.destination_state}<br/>
                                                        <span className="text-gray-500">Load: {load.load_id}</span>
                                                    </div>
                                                </Popup>
                                            </Marker>
                                            
                                            {/* Route Line */}
                                            <Polyline 
                                                positions={[originCoords, destCoords]}
                                                pathOptions={{ 
                                                    color: isSelected ? '#f97316' : getStatusColor(load.status),
                                                    weight: isSelected ? 4 : 2,
                                                    opacity: isSelected ? 1 : 0.6,
                                                    dashArray: load.status === 'in_transit' ? null : '10, 10'
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                            </MapContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Routes List */}
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="routes-list-card">
                    <CardHeader className="border-b border-zinc-800 pb-4">
                        <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                            <Truck className="w-5 h-5 text-blue-500 live-indicator" />
                            {showAllLoads ? 'All Loads' : 'In Transit'}
                            <Badge variant="outline" className="ml-auto border-zinc-700 text-zinc-400">
                                {displayLoads.length}
                            </Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : displayLoads.length === 0 ? (
                            <div className="empty-state py-12">
                                <Truck className="w-12 h-12 text-zinc-700 mb-4" />
                                <p className="text-zinc-500">No {showAllLoads ? 'loads' : 'active shipments'}</p>
                                <p className="text-zinc-600 text-sm mt-2">
                                    {showAllLoads ? 'Create loads to see them here' : 'Loads marked "In Transit" will appear here'}
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-zinc-800 max-h-[450px] overflow-y-auto">
                                {displayLoads.map((load) => (
                                    <div 
                                        key={load.load_id}
                                        className={`p-4 hover:bg-zinc-800/50 cursor-pointer transition-colors ${selectedLoad?.load_id === load.load_id ? 'bg-zinc-800/50 border-l-2 border-orange-500' : ''}`}
                                        onClick={() => setSelectedLoad(load)}
                                        data-testid={`route-item-${load.load_id}`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <span className="text-orange-500 font-mono text-sm">{load.load_id}</span>
                                            <div 
                                                className="w-2 h-2 rounded-full live-indicator"
                                                style={{ backgroundColor: getStatusColor(load.status) }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-sm">
                                                <div className="w-2 h-2 rounded-full bg-green-500" />
                                                <span className="text-zinc-300">{load.origin_city}, {load.origin_state}</span>
                                            </div>
                                            <div className="ml-1 border-l border-dashed border-zinc-700 h-3" />
                                            <div className="flex items-center gap-2 text-sm">
                                                <div className="w-2 h-2 rounded-full bg-red-500" />
                                                <span className="text-zinc-300">{load.destination_city}, {load.destination_state}</span>
                                            </div>
                                        </div>
                                        <div className="mt-3 flex items-center justify-between text-xs">
                                            <Badge 
                                                className="text-xs uppercase"
                                                style={{ 
                                                    backgroundColor: `${getStatusColor(load.status)}20`,
                                                    color: getStatusColor(load.status),
                                                    borderColor: `${getStatusColor(load.status)}50`
                                                }}
                                            >
                                                {load.status?.replace('_', ' ')}
                                            </Badge>
                                            <span className="text-zinc-500 font-mono">{load.distance?.toLocaleString() || '-'} mi</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Selected Load Details */}
            {selectedLoad && (
                <Card className="bg-zinc-900/50 border-zinc-800" data-testid="selected-route-card">
                    <CardHeader className="border-b border-zinc-800 pb-4">
                        <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide">
                            Route Details: {selectedLoad.load_id}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Origin</p>
                                <p className="text-white">{selectedLoad.origin_city}, {selectedLoad.origin_state}</p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Destination</p>
                                <p className="text-white">{selectedLoad.destination_city}, {selectedLoad.destination_state}</p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Equipment</p>
                                <p className="text-white">{selectedLoad.equipment_type}</p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Distance</p>
                                <p className="text-white font-mono">{selectedLoad.distance?.toLocaleString() || '-'} miles</p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Pickup Date</p>
                                <p className="text-white">{selectedLoad.pickup_date}</p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Delivery Date</p>
                                <p className="text-white">{selectedLoad.delivery_date || 'TBD'}</p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Rate</p>
                                <p className="text-green-400 font-mono">
                                    ${selectedLoad.rate?.toLocaleString() || '-'}
                                    {selectedLoad.rate_per_mile && (
                                        <span className="text-zinc-500 text-xs ml-2">
                                            (${selectedLoad.rate_per_mile}/mi)
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Status</p>
                                <Badge 
                                    className="text-xs uppercase"
                                    style={{ 
                                        backgroundColor: `${getStatusColor(selectedLoad.status)}20`,
                                        color: getStatusColor(selectedLoad.status),
                                        borderColor: `${getStatusColor(selectedLoad.status)}50`
                                    }}
                                >
                                    {selectedLoad.status?.replace('_', ' ')}
                                </Badge>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
